import { useEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Star, Share2, Plus, Check, Volume2, VolumeX, Play } from 'lucide-react';
import { buildFeedSlides, formatZAR, type ServiceDoc } from '@makhe/shared';
import { useAuth } from '@/stores/auth';
import { db } from '@/lib/firebase';
import { toast } from '@/components/ui/Toast';
import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';

export function VideoCard({
  service,
  active,
  feedVisible = true,
  muted,
  onToggleMute,
  onAutoplayBlocked,
}: {
  service: ServiceDoc;
  active: boolean;
  feedVisible?: boolean;
  muted: boolean;
  onToggleMute: () => void;
  onAutoplayBlocked?: () => void;
}) {
  // One entry per video slide (keyed by slide index) so the play/pause effect
  // controls the *current* slide's video — a single shared ref only ever pointed
  // at the last-rendered <video>, so a card with more than one video slide
  // misbehaved.
  const videoEls = useRef<Map<number, HTMLVideoElement>>(new Map());
  const carouselRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [burst, setBurst] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const tapTimer = useRef<number | undefined>(undefined);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const { firebaseUser, userDoc } = useAuth();
  const isFav = !!userDoc?.preferences?.favouriteSellers?.includes(service.sellerId);

  const slides = useMemo(() => buildFeedSlides(service.media), [service.media]);
  const currentSlide = slides[slideIndex];
  const onVideoSlide = currentSlide?.kind === 'video';

  // Track which horizontal slide is in view.
  useEffect(() => {
    const root = carouselRef.current;
    if (!root || slides.length <= 1) return;

    const onScroll = () => {
      const width = root.clientWidth;
      if (width <= 0) return;
      const idx = Math.round(root.scrollLeft / width);
      setSlideIndex(Math.max(0, Math.min(idx, slides.length - 1)));
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [slides.length]);

  const scrollToSlide = (index: number) => {
    const root = carouselRef.current;
    if (!root) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const width = root.clientWidth;
    if (width > 0) {
      root.scrollTo({ left: clamped * width, behavior: 'smooth' });
      setSlideIndex(clamped);
    }
  };

  const onSlidePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };

  const onSlidePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx > 12 || dy > 12) return;
    onTap();
  };

  // Reset carousel when this card scrolls out of the vertical feed — not when
  // the user navigates away and the feed is merely hidden.
  useEffect(() => {
    if (!feedVisible) return;
    if (!active) {
      setSlideIndex(0);
      setPaused(false);
      carouselRef.current?.scrollTo({ left: 0, behavior: 'instant' });
    }
  }, [active, feedVisible]);

  useEffect(() => {
    videoEls.current.forEach((el, idx) => {
      el.muted = muted;
      const shouldPlay = active && !paused && idx === slideIndex;
      if (shouldPlay) {
        const p = el.play();
        if (p)
          p.catch(() => {
            if (!muted) onAutoplayBlocked?.();
          });
      } else {
        el.pause();
      }
    });
  }, [active, muted, paused, slideIndex, onAutoplayBlocked]);

  const onTap = () => {
    if (tapTimer.current) {
      window.clearTimeout(tapTimer.current);
      tapTimer.current = undefined;
      doubleLike();
      return;
    }
    tapTimer.current = window.setTimeout(() => {
      tapTimer.current = undefined;
      if (onVideoSlide) setPaused((p) => !p);
    }, 220);
  };

  const setFav = async (next: boolean) => {
    if (!firebaseUser || next === isFav) return;
    await updateDoc(doc(db, 'users', firebaseUser.uid), {
      'preferences.favouriteSellers': next ? arrayUnion(service.sellerId) : arrayRemove(service.sellerId),
    }).catch(() => {});
  };
  const toggleFav = () => setFav(!isFav);

  const doubleLike = () => {
    void setFav(true);
    setBurst(true);
    window.setTimeout(() => setBurst(false), 700);
  };

  const share = async () => {
    const url = `${window.location.origin}/service/${service.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: service.title, url });
      } catch {
        /* user dismissed */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      } catch {
        /* ignore */
      }
    }
  };

  const minPrice = Math.min(...service.pricingTiers.map((t) => t.priceZARcents));
  const name = service.sellerSnapshot.displayName;
  const multiSlide = slides.length > 1;

  return (
    <article className="relative h-full w-full snap-start overflow-hidden bg-makhe-night">
      <div
        ref={carouselRef}
        className={
          multiSlide
            ? 'flex h-full w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
            : 'h-full w-full'
        }
      >
        {slides.map((slide, i) => (
          <div
            key={`${slide.kind}-${slide.url}`}
            data-slide-idx={i}
            className={`relative h-full shrink-0 ${multiSlide ? 'w-full snap-start' : 'w-full'}`}
            onPointerDown={onSlidePointerDown}
            onPointerUp={onSlidePointerUp}
          >
            {slide.kind === 'video' ? (
              <video
                ref={(el) => {
                  if (el) videoEls.current.set(i, el);
                  else videoEls.current.delete(i);
                }}
                src={slide.url}
                loop
                playsInline
                preload={active && i === slideIndex ? 'auto' : 'none'}
                className="h-full w-full object-cover"
                poster={slide.poster}
              />
            ) : (
              <img src={slide.url} alt="" className="h-full w-full object-cover" />
            )}
          </div>
        ))}
      </div>

      {active && onVideoSlide && paused && (
        <div className="pointer-events-none absolute inset-0 z-[2] grid place-items-center">
          <Play size={72} className="text-white/90 drop-shadow-2xl" fill="currentColor" />
        </div>
      )}

      {burst && (
        <div className="pointer-events-none absolute inset-0 z-[2] grid place-items-center">
          <Heart size={128} className="animate-ping text-white drop-shadow-2xl" fill="currentColor" />
        </div>
      )}

      {multiSlide && (
        <div className="absolute left-1/2 top-4 z-10 flex -translate-x-1/2 flex-col items-center gap-2">
          <div className="rounded-full bg-black/40 px-3 py-1 text-[11px] font-medium text-white backdrop-blur">
            {slideIndex + 1} / {slides.length}
          </div>
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => scrollToSlide(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === slideIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/45'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {multiSlide && (
        <>
          {slideIndex > 0 && (
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => scrollToSlide(slideIndex - 1)}
              className="absolute left-3 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white backdrop-blur"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {slideIndex < slides.length - 1 && (
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => scrollToSlide(slideIndex + 1)}
              className="absolute right-3 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white backdrop-blur"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-[45%] bg-gradient-to-t from-makhe-night via-makhe-night/40 to-transparent" />

      <div className="absolute bottom-7 right-3 z-10 flex flex-col items-center gap-5 text-white">
        <div className="relative mb-1">
          <Link to={`/seller/${service.sellerId}`} aria-label={`${name} profile`}>
            {service.sellerSnapshot.photoURL ? (
              <img
                src={service.sellerSnapshot.photoURL}
                alt={name}
                className="h-12 w-12 rounded-full object-cover ring-2 ring-white"
              />
            ) : (
              <span className="grid h-12 w-12 place-items-center rounded-full bg-makhe-primary font-display text-lg text-white ring-2 ring-white">
                {name.charAt(0)}
              </span>
            )}
          </Link>
          <button
            onClick={toggleFav}
            aria-label={isFav ? 'Unfollow creator' : 'Follow creator'}
            className="absolute -bottom-2 left-1/2 grid h-5 w-5 -translate-x-1/2 place-items-center rounded-full bg-makhe-primary text-white ring-2 ring-makhe-night transition-transform active:scale-90"
          >
            {isFav ? <Check size={12} /> : <Plus size={12} />}
          </button>
        </div>

        <RailItem onClick={toggleFav} label="Save" ariaLabel={isFav ? 'Unsave' : 'Save'}>
          <Heart
            size={28}
            fill={isFav ? 'currentColor' : 'none'}
            className={`transition-transform active:scale-125 ${isFav ? 'text-makhe-primary' : ''}`}
          />
        </RailItem>

        <Link
          to={`/service/${service.id}#reviews`}
          aria-label="Reviews"
          className="flex flex-col items-center gap-1.5"
        >
          <Star size={28} />
          <span className="text-[11px] font-semibold">{service.stats.totalReviews}</span>
        </Link>

        <RailItem onClick={share} label="Share" ariaLabel="Share">
          <Share2 size={26} />
        </RailItem>
      </div>

      <div className="absolute bottom-7 left-4 right-20 z-10 text-white">
        <Link to={`/seller/${service.sellerId}`} className="inline-flex items-center gap-1.5">
          <span className="font-display text-base italic">{name}</span>
          <Check size={15} className="rounded-full bg-makhe-ochre p-[2px] text-makhe-night" />
          {service.stats.totalReviews > 0 && (
            <span className="ml-1 flex items-center gap-0.5 text-xs text-white/80">
              <Star size={11} className="fill-makhe-ochre text-makhe-ochre" />
              {service.stats.averageRating.toFixed(1)}
            </span>
          )}
        </Link>
        <h3
          className="mt-2 font-display text-2xl leading-tight"
          style={{ fontVariationSettings: '"opsz" 72, "wght" 450' }}
        >
          {service.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-white/85">{service.description}</p>
        <Link
          to={`/service/${service.id}`}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[11px] uppercase tracking-widest text-makhe-night shadow-lift transition-transform hover:-translate-y-0.5"
        >
          Book — from{' '}
          <span className="font-mono normal-case tracking-normal text-makhe-primary">{formatZAR(minPrice)}</span>
        </Link>
      </div>

      {onVideoSlide && (
        <button
          onClick={onToggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white backdrop-blur"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}
    </article>
  );
}

function RailItem({
  children,
  label,
  onClick,
  ariaLabel,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button onClick={onClick} aria-label={ariaLabel} className="flex flex-col items-center gap-1.5">
      {children}
      <span className="text-[11px] font-semibold">{label}</span>
    </button>
  );
}
